/**
  ============================================================================
  GLOBETROTTER ARTISAN INDIA — CATALOGUE INTERACTIONS & DISCOVERY
  Phase 5: Client-Side Search, Debounced Filtering, Accessible Galleries & URL Sync
  Includes dynamic preview router for unpersisted local dev routes.
  ============================================================================
**/

(function () {
  'use strict';

  // Read JSON catalogue data embedded by snippets/gtai-catalogue-data.liquid
  function getCatalogueData() {
    const dataEl = document.getElementById('gtai-catalogue-data');
    if (!dataEl) return [];
    try {
      return JSON.parse(dataEl.textContent || '[]');
    } catch (e) {
      console.warn('Could not parse GTAI catalogue data JSON', e);
      return [];
    }
  }

  // Client-Side Staging Router for dev server 404 fallback
  function initDevStagingRouter() {
    const notFoundSection = document.querySelector('.gtai-404-section');
    if (!notFoundSection) return;

    const path = window.location.pathname.replace(/\/+$/, '');
    const catalogue = getCatalogueData();
    if (!catalogue || catalogue.length === 0) return;

    // 1. Check if PDP route: /pages/objects/{handle}
    if (path.startsWith('/pages/objects/')) {
      const handle = path.split('/').pop();
      const product = catalogue.find(
        (item) => item.proposed_handle === handle || item.sku.toLowerCase() === handle.toLowerCase()
      );

      if (product) {
        renderStagingPDP(notFoundSection, product, catalogue);
        return;
      }
    }

    // 2. Check if All Objects route: /pages/objects
    if (path === '/pages/objects') {
      renderStagingCatalogue(notFoundSection, catalogue, 'all');
      return;
    }

    // 3. Check if dedicated Collection route: /pages/{collection-handle}
    const collectionHandles = {
      '/pages/animal-reverie': 'animal-reverie',
      '/pages/figure-studies': 'figure-studies',
      '/pages/form-and-symbol': 'form-and-symbol',
      '/pages/objects-of-use': 'objects-of-use',
      '/pages/wall-stories': 'wall-stories',
    };

    if (collectionHandles[path]) {
      const colHandle = collectionHandles[path];
      renderStagingCollection(notFoundSection, catalogue, colHandle);
      return;
    }
  }

  function generateCardHtml(item) {
    const mailtoSubject = encodeURIComponent(`Wholesale Pricing Enquiry — ${item.sku} — ${item.title}`);
    const mailtoBody = encodeURIComponent(
      `Hello Globetrotter Artisan India Team,\n\nI would like to request wholesale pricing and availability for the following catalogue object:\n\nObject: ${item.title}\nSKU: ${item.sku}\nCollection: ${item.collection}\nProduct Type: ${item.product_type}\n\nCompany / Studio: [Your Company Name]\nDelivery Destination: [City, Country]\nRequested Quantity: [Enter Quantity]\nAdditional Requirements: [Project specifications or questions]\n\nThank you.`
    );
    const mailtoHref = `mailto:connect@globetrotterartisanindia.com?subject=${mailtoSubject}&body=${mailtoBody}`;
    const detailUrl = item.proposed_url;

    return `
      <article
        class="gtai-product-card"
        data-gtai-catalogue-card
        data-sku="${item.sku}"
        data-title="${item.title}"
        data-collection="${item.collection}"
        data-collection-handle="${item.collection_handle}"
        data-type="${item.product_type}"
        data-placement="${item.placement}"
        aria-label="${item.title} (${item.sku})"
      >
        <div class="gtai-product-card__media">
          <a href="${detailUrl}" data-gtai-detail-link data-sku="${item.sku}" tabindex="-1" aria-hidden="true" class="gtai-product-card__img-link">
            <img src="${item.hero_image}" alt="${item.title} (${item.sku}) — Primary view" class="gtai-product-card__img" width="600" height="750" loading="lazy" decoding="async">
            ${item.alternate_image ? `<img src="${item.alternate_image}" alt="${item.title} (${item.sku}) — Alternate view" class="gtai-product-card__img gtai-product-card__img--alternate" width="600" height="750" loading="lazy" decoding="async">` : ''}
          </a>
          <span class="gtai-product-card__sku">${item.sku}</span>
        </div>
        <div class="gtai-product-card__info">
          <span class="gtai-product-card__category">${item.collection} · ${item.product_type}</span>
          <h3 class="gtai-product-card__title">
            <a href="${detailUrl}" data-gtai-detail-link data-sku="${item.sku}" class="gtai-product-card__title-link">${item.title}</a>
          </h3>
          <span class="gtai-product-card__pricing">Ask for Pricing</span>
          <div class="gtai-product-card__actions">
            <a href="${mailtoHref}" class="gtai-btn gtai-btn--primary gtai-btn--compact gtai-product-card__btn" aria-label="Request Pricing for ${item.title} (${item.sku})">Ask for Pricing</a>
            <a href="${detailUrl}" data-gtai-detail-link data-sku="${item.sku}" class="gtai-btn gtai-btn--secondary gtai-btn--compact gtai-product-card__btn-view" aria-label="View details for ${item.title}">View Details</a>
          </div>
        </div>
      </article>
    `;
  }

  function renderStagingCatalogue(container, catalogue, initialFilter) {
    document.title = 'The Collection — Globetrotter Artisan India';
    const cardsHtml = catalogue.map(generateCardHtml).join('');

    container.outerHTML = `
      <section class="gtai-catalogue-section" aria-labelledby="CatalogueHeading" data-gtai-catalogue-root>
        <div class="gtai-container">
          <div class="gtai-catalogue-header">
            <nav class="gtai-breadcrumbs" aria-label="Breadcrumbs">
              <ol class="gtai-breadcrumbs__list">
                <li class="gtai-breadcrumbs__item"><a href="/" class="gtai-breadcrumbs__link">Home</a></li>
                <li class="gtai-breadcrumbs__separator" aria-hidden="true">/</li>
                <li class="gtai-breadcrumbs__item" aria-current="page">The Collection</li>
              </ol>
            </nav>
            <span class="gtai-eyebrow">COMPLETE CATALOGUE</span>
            <h1 id="CatalogueHeading" class="gtai-heading-1">The Collection</h1>
            <p class="gtai-section-subtitle">
              Objects with character, crafted for considered interiors. Discover 15 handcrafted sculptures, vessels, and architectural wall pieces available exclusively via studio quotation.
            </p>
          </div>

          <div class="gtai-catalogue-toolbar" role="region" aria-label="Catalogue discovery and filters">
            <div class="gtai-filter-pills" role="toolbar" aria-label="Filter by collection">
              <button type="button" class="gtai-filter-pill is-active" data-filter-collection="all" aria-pressed="true">All Objects <span class="gtai-pill-count">(15)</span></button>
              <button type="button" class="gtai-filter-pill" data-filter-collection="animal-reverie" aria-pressed="false">Animal Reverie <span class="gtai-pill-count">(5)</span></button>
              <button type="button" class="gtai-filter-pill" data-filter-collection="figure-studies" aria-pressed="false">Figure Studies <span class="gtai-pill-count">(3)</span></button>
              <button type="button" class="gtai-filter-pill" data-filter-collection="form-and-symbol" aria-pressed="false">Form + Symbol <span class="gtai-pill-count">(3)</span></button>
              <button type="button" class="gtai-filter-pill" data-filter-collection="objects-of-use" aria-pressed="false">Objects of Use <span class="gtai-pill-count">(2)</span></button>
              <button type="button" class="gtai-filter-pill" data-filter-collection="wall-stories" aria-pressed="false">Wall Stories <span class="gtai-pill-count">(2)</span></button>
            </div>

            <div class="gtai-catalogue-status-bar">
              <p id="CatalogueResultsCount" class="gtai-results-count" role="status" aria-live="polite" data-gtai-results-count>
                Showing <strong>15</strong> of 15 objects
              </p>
              <button type="button" class="gtai-reset-filters-btn" data-gtai-reset-btn hidden>Reset All Filters</button>
            </div>
          </div>

          <div class="gtai-catalogue-grid" data-gtai-catalogue-grid aria-label="Catalogue objects grid">
            ${cardsHtml}
          </div>

          <div class="gtai-catalogue-empty" data-gtai-empty-state role="region" aria-live="polite" hidden>
            <div class="gtai-empty-icon" aria-hidden="true">&#9671;</div>
            <h2 class="gtai-empty-title">No matching objects found</h2>
            <p class="gtai-empty-desc">No works are currently available in this selected collection. Reset filters to view all handcrafted catalogue objects.</p>
            <button type="button" class="gtai-btn gtai-btn--secondary" data-gtai-empty-reset>View All 15 Objects</button>
          </div>
        </div>
      </section>
    `;

    initCatalogueDiscovery();
  }

  function renderStagingCollection(container, catalogue, colHandle) {
    const meta = {
      'animal-reverie': {
        title: 'Animal Reverie',
        count: 5,
        hero: catalogue.find((i) => i.sku === 'GT-001')?.hero_image || '',
        desc: 'Expressive animal sculptures, zoomorphic tabletop studies, and carved sculptural bowls representing natural vitality, classical cadence, and handcrafted poise.',
      },
      'figure-studies': {
        title: 'Figure Studies',
        count: 3,
        hero: catalogue.find((i) => i.sku === 'GT-068')?.hero_image || '',
        desc: 'Figurative portraits and sculptural narratives with emotive poise, contemplative posture, and botanical crowns for considered collector shelves and niches.',
      },
      'form-and-symbol': {
        title: 'Form + Symbol',
        count: 3,
        hero: catalogue.find((i) => i.sku === 'GT-096')?.hero_image || '',
        desc: 'Contemporary reduced forms, faceted symbolic animal studies, and abstract sculptural totems exploring geometry, verdigris patinas, and elemental balance.',
      },
      'objects-of-use': {
        title: 'Objects of Use',
        count: 2,
        hero: catalogue.find((i) => i.sku === 'GT-118')?.hero_image || '',
        desc: 'Decorative vessels, handcrafted sculpted trays, and functional tabletop statements designed for hospitality vignettes, centre tables, and considered entryways.',
      },
      'wall-stories': {
        title: 'Wall Stories',
        count: 2,
        hero: catalogue.find((i) => i.sku === 'GT-138')?.hero_image || '',
        desc: 'Dimensioned wall reliefs and suspended sculptural statements crafted for architectural feature walls, entry vestibules, and corridor focal points.',
      },
    }[colHandle];

    if (!meta) return;

    document.title = `${meta.title} Collection — Globetrotter Artisan India`;
    const colItems = catalogue.filter((i) => i.collection_handle === colHandle);
    const cardsHtml = colItems.map(generateCardHtml).join('');

    container.outerHTML = `
      <section class="gtai-collection-page" aria-labelledby="ColPageHeading" data-gtai-collection-page data-current-family="${colHandle}">
        <header class="gtai-collection-hero">
          <div class="gtai-container">
            <nav class="gtai-breadcrumbs" aria-label="Breadcrumbs">
              <ol class="gtai-breadcrumbs__list">
                <li class="gtai-breadcrumbs__item"><a href="/" class="gtai-breadcrumbs__link">Home</a></li>
                <li class="gtai-breadcrumbs__separator" aria-hidden="true">/</li>
                <li class="gtai-breadcrumbs__item"><a href="/collections" class="gtai-breadcrumbs__link">Collections</a></li>
                <li class="gtai-breadcrumbs__separator" aria-hidden="true">/</li>
                <li class="gtai-breadcrumbs__item" aria-current="page">${meta.title}</li>
              </ol>
            </nav>
            <div class="gtai-collection-hero__grid">
              <div class="gtai-collection-hero__content">
                <span class="gtai-eyebrow">DESIGN FAMILY · ${meta.count} WORKS</span>
                <h1 id="ColPageHeading" class="gtai-heading-1 gtai-collection-hero__title">${meta.title}</h1>
                <p class="gtai-collection-hero__desc">${meta.desc}</p>
                <div class="gtai-collection-hero__meta">
                  <span class="gtai-badge gtai-badge--subtle">${meta.count} Objects Available for Trade</span>
                  <span class="gtai-badge gtai-badge--subtle">Studio Quotation Model</span>
                </div>
              </div>
              <div class="gtai-collection-hero__visual">
                <img src="${meta.hero}" alt="${meta.title} collection" class="gtai-collection-hero__img" width="700" height="875" loading="eager" decoding="async">
                <span class="gtai-collection-hero__badge">${meta.title}</span>
              </div>
            </div>
          </div>
        </header>

        <div class="gtai-container gtai-collection-grid-wrap">
          <div class="gtai-collection-grid-header">
            <h2 class="gtai-heading-2">Collection Works (${meta.count})</h2>
            <a href="/pages/objects" class="gtai-link-subtle">View All 15 Catalogue Objects &rarr;</a>
          </div>
          <div class="gtai-catalogue-grid gtai-collection-grid">
            ${cardsHtml}
          </div>
        </div>

        <nav class="gtai-related-families" aria-labelledby="OtherFamiliesHeading">
          <div class="gtai-container">
            <h2 id="OtherFamiliesHeading" class="gtai-heading-3 gtai-text-center">Explore Other Design Families</h2>
            <div class="gtai-family-nav-pills">
              <a href="/pages/animal-reverie" class="gtai-family-pill${colHandle === 'animal-reverie' ? ' is-current' : ''}">Animal Reverie (5)</a>
              <a href="/pages/figure-studies" class="gtai-family-pill${colHandle === 'figure-studies' ? ' is-current' : ''}">Figure Studies (3)</a>
              <a href="/pages/form-and-symbol" class="gtai-family-pill${colHandle === 'form-and-symbol' ? ' is-current' : ''}">Form + Symbol (3)</a>
              <a href="/pages/objects-of-use" class="gtai-family-pill${colHandle === 'objects-of-use' ? ' is-current' : ''}">Objects of Use (2)</a>
              <a href="/pages/wall-stories" class="gtai-family-pill${colHandle === 'wall-stories' ? ' is-current' : ''}">Wall Stories (2)</a>
            </div>
          </div>
        </nav>
      </section>
    `;
  }

  function renderStagingPDP(container, item, catalogue) {
    document.title = `${item.title} (${item.sku}) — Globetrotter Artisan India`;
    const placements = (item.placement || '').split(';').map((p) => p.trim()).filter(Boolean);
    const placementTagsHtml = placements
      .map((p) => `<span class="gtai-placement-tag">${p}</span>`)
      .join('');

    const mailtoSubject = encodeURIComponent(`Wholesale Pricing Enquiry — ${item.sku} — ${item.title}`);
    const mailtoBody = encodeURIComponent(
      `Hello Globetrotter Artisan India Team,\n\nI would like to request wholesale pricing and availability for the following catalogue object:\n\nObject: ${item.title}\nSKU: ${item.sku}\nCollection: ${item.collection}\nProduct Type: ${item.product_type}\n\nBuyer Company / Studio: [Your Company / Design Studio Name]\nDelivery Destination: [City, Country]\nRequested Quantity: [Enter Quantity Required]\nAdditional Requirements: [Project specifications or bespoke notes]\n\nThank you.`
    );
    const mailtoHref = `mailto:connect@globetrotterartisanindia.com?subject=${mailtoSubject}&body=${mailtoBody}`;

    const relatedItems = catalogue
      .filter((i) => i.collection_handle === item.collection_handle && i.sku !== item.sku)
      .slice(0, 3);
    const relatedCardsHtml = relatedItems.map(generateCardHtml).join('');

    container.outerHTML = `
      <article class="gtai-pdp-section" aria-labelledby="PdpHeading" data-gtai-pdp-root data-sku="${item.sku}">
        <div class="gtai-container">
          <nav class="gtai-breadcrumbs" aria-label="Breadcrumbs">
            <ol class="gtai-breadcrumbs__list">
              <li class="gtai-breadcrumbs__item"><a href="/" class="gtai-breadcrumbs__link">Home</a></li>
              <li class="gtai-breadcrumbs__separator" aria-hidden="true">/</li>
              <li class="gtai-breadcrumbs__item"><a href="/pages/objects" class="gtai-breadcrumbs__link">The Collection</a></li>
              <li class="gtai-breadcrumbs__separator" aria-hidden="true">/</li>
              <li class="gtai-breadcrumbs__item"><a href="/pages/${item.collection_handle}" class="gtai-breadcrumbs__link">${item.collection}</a></li>
              <li class="gtai-breadcrumbs__separator" aria-hidden="true">/</li>
              <li class="gtai-breadcrumbs__item" aria-current="page">${item.title}</li>
            </ol>
          </nav>

          <div class="gtai-pdp-split">
            <div class="gtai-pdp-split__media">
              <div class="gtai-gallery" data-gtai-gallery role="region" aria-label="Product photography gallery for ${item.title}">
                <div class="gtai-gallery__main-wrap">
                  <img src="${item.hero_image}" alt="${item.title} (${item.sku}) — Primary view" class="gtai-gallery__main-img" data-gtai-gallery-main width="1000" height="1250" loading="eager" decoding="async">
                  <div class="gtai-gallery__badge">${item.sku}</div>
                </div>
                <div class="gtai-gallery__thumbs" role="tablist" aria-label="Alternative views">
                  <button type="button" role="tab" aria-selected="true" aria-label="View 1: Primary angle of ${item.title}" class="gtai-gallery__thumb is-active" data-gtai-gallery-thumb data-target-src="${item.hero_image}" data-target-alt="${item.title} (${item.sku}) — Primary view">
                    <img src="${item.hero_image}" alt="" class="gtai-gallery__thumb-img" width="160" height="200" loading="lazy" decoding="async" aria-hidden="true">
                    <span class="gtai-gallery__thumb-label">Hero View</span>
                  </button>
                  ${
                    item.alternate_image
                      ? `
                    <button type="button" role="tab" aria-selected="false" aria-label="View 2: Alternate angle of ${item.title}" class="gtai-gallery__thumb" data-gtai-gallery-thumb data-target-src="${item.alternate_image}" data-target-alt="${item.title} (${item.sku}) — Alternate view">
                      <img src="${item.alternate_image}" alt="" class="gtai-gallery__thumb-img" width="160" height="200" loading="lazy" decoding="async" aria-hidden="true">
                      <span class="gtai-gallery__thumb-label">Alternate View</span>
                    </button>
                  `
                      : ''
                  }
                </div>
              </div>
            </div>

            <div class="gtai-pdp-split__info">
              <header class="gtai-pdp-header">
                <div class="gtai-pdp-eyebrow-row">
                  <a href="/pages/${item.collection_handle}" class="gtai-pdp-collection-link">${item.collection.toUpperCase()}</a>
                  <span class="gtai-pdp-sku-badge">${item.sku}</span>
                </div>
                <h1 id="PdpHeading" class="gtai-heading-1 gtai-pdp-title">${item.title}</h1>
                <div class="gtai-pdp-meta-bar">
                  <span class="gtai-pdp-category">${item.product_type}</span>
                  <span class="gtai-pdp-pricing">Ask for Pricing</span>
                </div>
              </header>

              <div class="gtai-pdp-body">
                <p class="gtai-pdp-description">Product details, availability and wholesale pricing are available on request.</p>
                ${
                  placementTagsHtml
                    ? `
                  <div class="gtai-pdp-placements">
                    <h2 class="gtai-pdp-placement-label">Suggested Architectural Placement</h2>
                    <div class="gtai-pdp-placement-tags">${placementTagsHtml}</div>
                  </div>
                `
                    : ''
                }
                <div class="gtai-pdp-notice">
                  <div class="gtai-pdp-notice__icon" aria-hidden="true">&#9671;</div>
                  <p class="gtai-pdp-notice__text">Globetrotter Artisan India operates on an exclusive commercial enquiry model. Specification dossiers, dimensional sheets, and wholesale quotations are provided directly to design professionals, trade buyers, and collectors.</p>
                </div>
              </div>

              <div class="gtai-pdp-actions">
                <a href="${mailtoHref}" class="gtai-btn gtai-btn--primary gtai-btn--large gtai-pdp-cta-btn" aria-label="Request Pricing for ${item.title} (${item.sku})">Request Pricing</a>
                <div class="gtai-pdp-contact-alt">
                  <span>Direct studio correspondence:</span>
                  <a href="mailto:connect@globetrotterartisanindia.com" class="gtai-pdp-email-link">connect@globetrotterartisanindia.com</a>
                </div>
              </div>
            </div>
          </div>

          ${
            relatedCardsHtml
              ? `
            <section class="gtai-pdp-related" aria-labelledby="RelatedHeading">
              <div class="gtai-pdp-related__header">
                <span class="gtai-eyebrow">SAME FAMILY</span>
                <h2 id="RelatedHeading" class="gtai-heading-2">Related in ${item.collection}</h2>
              </div>
              <div class="gtai-catalogue-grid gtai-related-grid">
                ${relatedCardsHtml}
              </div>
            </section>
          `
              : ''
          }
        </div>
      </article>
    `;

    initGallerySwitchers();
  }

  // Catalogue Discovery Search & Filters logic
  function initCatalogueDiscovery() {
    const root = document.querySelector('[data-gtai-catalogue-root]');
    if (!root) return;

    const filterPills = root.querySelectorAll('[data-filter-collection]');
    const cards = root.querySelectorAll('[data-gtai-catalogue-card]');
    const resultsCountEl = root.querySelector('[data-gtai-results-count]');
    const resetBtn = root.querySelector('[data-gtai-reset-btn]');
    const emptyState = root.querySelector('[data-gtai-empty-state]');
    const emptyResetBtn = root.querySelector('[data-gtai-empty-reset]');

    let activeFilter = 'all';

    // Read initial state from URL query parameters
    const params = new URLSearchParams(window.location.search);
    const initialCollection = params.get('collection');

    if (initialCollection) {
      const matchPill = Array.from(filterPills).find(
        (p) => p.getAttribute('data-filter-collection') === initialCollection.toLowerCase()
      );
      if (matchPill) {
        activeFilter = initialCollection.toLowerCase();
        filterPills.forEach((p) => {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });
        matchPill.classList.add('is-active');
        matchPill.setAttribute('aria-pressed', 'true');
      }
    }

    function syncUrl() {
      const url = new URL(window.location.href);
      if (activeFilter && activeFilter !== 'all') {
        url.searchParams.set('collection', activeFilter);
      } else {
        url.searchParams.delete('collection');
      }
      url.searchParams.delete('q');
      url.searchParams.delete('search');

      if (window.history.pushState) {
        window.history.replaceState({ filter: activeFilter }, '', url.toString());
      }
    }

    function applyFilter() {
      let visibleCount = 0;
      const totalCount = cards.length;

      cards.forEach((card) => {
        const collection = (card.getAttribute('data-collection') || '').toLowerCase();
        const collectionHandle = (card.getAttribute('data-collection-handle') || '').toLowerCase();

        // Check collection filter match
        let matchesCollection = false;
        if (activeFilter === 'all') {
          matchesCollection = true;
        } else if (
          collectionHandle === activeFilter ||
          collection.includes(activeFilter.replace(/-/g, ' '))
        ) {
          matchesCollection = true;
        }

        if (matchesCollection) {
          card.style.display = '';
          visibleCount++;
        } else {
          card.style.display = 'none';
        }
      });

      // Update count & announcement
      if (resultsCountEl) {
        if (visibleCount === totalCount) {
          resultsCountEl.innerHTML = `Showing <strong>${visibleCount}</strong> of ${totalCount} objects`;
        } else {
          resultsCountEl.innerHTML = `Showing <strong>${visibleCount}</strong> objects in collection (out of ${totalCount})`;
        }
      }

      // Empty state toggle
      if (emptyState) {
        emptyState.hidden = visibleCount > 0;
      }

      // Reset button visibility
      const isFiltered = activeFilter !== 'all';
      if (resetBtn) {
        resetBtn.hidden = !isFiltered;
      }

      syncUrl();
    }

    // Filter pill events
    filterPills.forEach((pill) => {
      pill.addEventListener('click', function () {
        const targetCollection = this.getAttribute('data-filter-collection');
        if (activeFilter === targetCollection) return;

        filterPills.forEach((p) => {
          p.classList.remove('is-active');
          p.setAttribute('aria-pressed', 'false');
        });

        this.classList.add('is-active');
        this.setAttribute('aria-pressed', 'true');
        activeFilter = targetCollection;

        applyFilter();
      });
    });

    // Reset all filters function
    function resetAll() {
      activeFilter = 'all';

      filterPills.forEach((p) => {
        const isAll = p.getAttribute('data-filter-collection') === 'all';
        p.classList.toggle('is-active', isAll);
        p.setAttribute('aria-pressed', isAll ? 'true' : 'false');
      });

      applyFilter();
    }

    if (resetBtn) resetBtn.addEventListener('click', resetAll);
    if (emptyResetBtn) emptyResetBtn.addEventListener('click', resetAll);

    // Initial filter calculation
    applyFilter();

    // Listen to browser popstate for back/forward navigation
    window.addEventListener('popstate', function () {
      const popParams = new URLSearchParams(window.location.search);
      const popCol = popParams.get('collection') || 'all';

      activeFilter = popCol;

      filterPills.forEach((p) => {
        const match = p.getAttribute('data-filter-collection') === popCol;
        p.classList.toggle('is-active', match);
        p.setAttribute('aria-pressed', match ? 'true' : 'false');
      });

      applyFilter();
    });
  }

  // Gallery Thumbnail Swapper
  function initGallerySwitchers() {
    const galleries = document.querySelectorAll('[data-gtai-gallery]');
    galleries.forEach((gallery) => {
      const mainImg = gallery.querySelector('[data-gtai-gallery-main]');
      const thumbs = gallery.querySelectorAll('[data-gtai-gallery-thumb]');

      if (!mainImg || thumbs.length === 0) return;

      thumbs.forEach((thumb, index) => {
        function activateThumb() {
          const targetSrc = thumb.getAttribute('data-target-src');
          const targetAlt = thumb.getAttribute('data-target-alt');

          if (!targetSrc) return;

          // Smooth fade transition
          mainImg.style.opacity = '0.3';
          setTimeout(() => {
            mainImg.src = targetSrc;
            if (targetAlt) mainImg.alt = targetAlt;
            mainImg.style.opacity = '1';
          }, 150);

          thumbs.forEach((t) => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });

          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');
        }

        thumb.addEventListener('click', activateThumb);

        // Keyboard arrow navigation
        thumb.addEventListener('keydown', (e) => {
          let nextIndex = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            nextIndex = (index + 1) % thumbs.length;
          } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            nextIndex = (index - 1 + thumbs.length) % thumbs.length;
          }

          if (nextIndex !== null) {
            e.preventDefault();
            thumbs[nextIndex].focus();
            thumbs[nextIndex].click();
          }
        });
      });
    });
  }

  // Main initialisation
  function init() {
    initDevStagingRouter();
    initCatalogueDiscovery();
    initGallerySwitchers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
